import React, { useState, useEffect } from 'react';
import { Bell, Check, X, Eye, Calendar, Video, ExternalLink, AlertCircle, Info, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format, isToday, isTomorrow, differenceInHours } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
  related_type?: string;
  related_id?: string;
  priority?: 'urgent' | 'important' | 'info';
  action_label?: string;
  action_url?: string;
}

interface WebinarSession {
  id: string;
  course_id: string;
  session_date: string;
  duration_minutes: number;
  title: string;
  meeting_url?: string;
  meeting_platform?: string;
  courses?: {
    title: string;
  };
}

export function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [webinars, setWebinars] = useState<WebinarSession[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [webinarCount, setWebinarCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      fetchUpcomingWebinars();
    }
  }, [user?.id]);

  const fetchUpcomingWebinars = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('webinar_sessions')
        .select(`
          *,
          courses!inner(
            title,
            enrollments!inner(participant_id)
          )
        `)
        .eq('courses.enrollments.participant_id', user.id)
        .gte('session_date', new Date().toISOString())
        .order('session_date', { ascending: true })
        .limit(5);

      if (error) throw error;

      const upcoming = (data || []).filter((session) => {
        const hoursUntil = differenceInHours(new Date(session.session_date), new Date());
        return hoursUntil <= 168; // Next 7 days
      });

      setWebinars(upcoming);
      
      const urgent = upcoming.filter((session) => {
        const sessionDate = new Date(session.session_date);
        return isToday(sessionDate) || isTomorrow(sessionDate);
      });
      setWebinarCount(urgent.length);
    } catch (error) {
      console.error('Error fetching webinars:', error);
    }
  };

  const getTimeLabel = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today at ${format(date, 'h:mm a')}`;
    }
    if (isTomorrow(date)) {
      return `Tomorrow at ${format(date, 'h:mm a')}`;
    }
    return format(date, 'PPp');
  };

  const fetchNotifications = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.read).length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to update notification');
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user?.id)
        .eq('read', false);

      if (error) throw error;

      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to update notifications');
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'important': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getPriorityIcon = (priority?: string) => {
    switch (priority) {
      case 'urgent': return <AlertCircle className="h-4 w-4" />;
      case 'important': return <Zap className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'warning': return 'text-orange-600 bg-orange-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return <Check className="h-4 w-4" />;
      case 'error': return <X className="h-4 w-4" />;
      case 'warning': return <Eye className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const handleNotificationAction = (notification: Notification) => {
    if (notification.action_url) {
      navigate(notification.action_url);
      markAsRead(notification.id);
      setOpen(false);
    }
  };

  const urgentNotifications = notifications.filter(n => n.priority === 'urgent' && !n.read);
  const importantNotifications = notifications.filter(n => n.priority === 'important' && !n.read);
  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : activeTab === 'urgent'
    ? notifications.filter(n => n.priority === 'urgent')
    : notifications.filter(n => !n.read);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {(unreadCount + webinarCount) > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {(unreadCount + webinarCount) > 99 ? '99+' : (unreadCount + webinarCount)}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-semibold">Notifications</h3>
            {(urgentNotifications.length > 0 || importantNotifications.length > 0) && (
              <div className="flex gap-2 mt-1">
                {urgentNotifications.length > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {urgentNotifications.length} Urgent
                  </Badge>
                )}
                {importantNotifications.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {importantNotifications.length} Important
                  </Badge>
                )}
              </div>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-3 rounded-none border-b">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="urgent" className="text-xs">Urgent</TabsTrigger>
            <TabsTrigger value="unread" className="text-xs">Unread ({unreadCount})</TabsTrigger>
          </TabsList>
        
          <TabsContent value={activeTab} className="m-0">
            <ScrollArea className="h-96">
          <div className="p-2 space-y-2">
            {/* Upcoming Webinars Section */}
            {webinars.length > 0 && (
              <>
                <div className="px-2 py-1">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <Video className="h-3 w-3" />
                    Upcoming Webinars
                  </h4>
                </div>
                {webinars.map((webinar) => (
                  <Card key={webinar.id} className="border-primary/20 bg-primary/5">
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <Video className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0 space-y-1">
                          <h5 className="text-sm font-medium line-clamp-1">{webinar.title}</h5>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {webinar.courses?.title}
                          </p>
                          <div className="flex items-center gap-1 text-xs">
                            <Calendar className="h-3 w-3" />
                            <span>{getTimeLabel(webinar.session_date)}</span>
                          </div>
                          {webinar.meeting_url && (
                            <a
                              href={webinar.meeting_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                              onClick={() => setOpen(false)}
                            >
                              <ExternalLink className="h-3 w-3" />
                              Join Meeting
                            </a>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Separator className="my-2" />
              </>
            )}

            {/* General Notifications */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading...</div>
              </div>
            ) : notifications.length === 0 && webinars.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="h-12 w-12 text-muted-foreground mb-2" />
                <div className="text-sm text-muted-foreground">No notifications yet</div>
              </div>
            ) : filteredNotifications.length > 0 ? (
              <div className="space-y-2">
                {filteredNotifications.map((notification) => (
                  <Card 
                    key={notification.id} 
                    className={`transition-colors ${
                      !notification.read ? getPriorityColor(notification.priority) : 'hover:bg-muted/50'
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-1 rounded-full ${getTypeColor(notification.type)}`}>
                          {notification.priority ? getPriorityIcon(notification.priority) : getTypeIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              {notification.priority && (
                                <Badge 
                                  variant={notification.priority === 'urgent' ? 'destructive' : 'secondary'} 
                                  className="text-xs mb-1"
                                >
                                  {notification.priority}
                                </Badge>
                              )}
                              <h4 className="text-sm font-medium leading-tight">
                                {notification.title}
                              </h4>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-primary rounded-full mt-1 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="text-xs text-muted-foreground">
                              {new Date(notification.created_at).toLocaleDateString()} at{' '}
                              {new Date(notification.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <div className="flex gap-1">
                              {notification.action_label && notification.action_url && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-xs"
                                  onClick={() => handleNotificationAction(notification)}
                                >
                                  {notification.action_label}
                                  <ArrowRight className="h-3 w-3 ml-1" />
                                </Button>
                              )}
                              {!notification.read && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => markAsRead(notification.id)}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="h-12 w-12 text-muted-foreground mb-2" />
                <div className="text-sm text-muted-foreground">No notifications in this category</div>
              </div>
            )}
            </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
        
        {notifications.length > 0 && (
          <div className="p-4 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setOpen(false)}
            >
              View All Notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}